import { DataTable } from '@/components/data-table';
import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { LoadingOverlay } from '@/components/ui/loading-overlay';

import { CascadingSelect, type CascadingOption } from '@/components/cascading-select';
import { useAuth } from '@/contexts/AuthContext';
import { createClassroom, createQuiz, publishAssignment, getTopicList, useGetKnowledgePointList, getQuestionInfo, updateShareGrantees } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { getSubjectDisplayName } from '@/hooks/use-subject-name';
import { QuestionInfoExt, QuestionLabel } from '@/services/generatedAPIs.schemas';

import { format } from 'date-fns';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown, Pencil } from 'lucide-react';
import { installMathFieldWrapper } from '@/components/readonly-question-display';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AssignmentBasicInfo } from './AssignmentPage';
import { ROUTE } from '@/routes/constants';
import { difficultyLevelToText, getQuestionText, getStandardAnswerText } from '@/lib/utils';
import { isEN } from '@/utils/i18n';
import { convertSpacesToLatex } from '@/utils/preprocess';
import { HomeworkPreviewDialog, PreviewMode } from '@/components/homework-preview-dialog';
import { toast } from 'sonner';
import { saveHomeworkDraft, removeHomeworkDraft } from '@/utils/homework-draft-utils';
import { useAsyncAction } from '@/hooks/use-async-action';

type TableData = {
  questionInfo: QuestionInfoExt,
  questionText: string,
  difficulty: number,
  topics: string[],
  knowledgePoints: string[],
}

const AICompileAssignmentResultPage = () => {
  const { getCurrentUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isFetching, setIsFetching] = useState(false);
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });

  const state = useLocation().state as AssignmentBasicInfo | null;

  useEffect(() => {
    if (!state) {
      navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
    }
  }, [state, navigate]);

  if (!state) {
    return <LoadingOverlay />;
  }

  const { subjectInfos } = useSubjectList();
  const subjectName = getSubjectDisplayName(subjectInfos, state.subjectId, undefined, i18n.language);
  let [selectedQuestions, setSelectedQuestions] = useState<QuestionInfoExt[]>(
    state.questionInfoList?.flat() ?? []
  );

  // Topic filtering states
  const [allTopics, setAllTopics] = useState<QuestionLabel[]>([]);
  const [currentTopics, setCurrentTopics] = useState<QuestionLabel[]>([]);
  const [knowledgePointFilter, setKnowledgePointFilter] = useState<string[]>([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  // Draft ID if this page was resumed from a draft
  const draftId = (state as any)?.draftId;

  // Fetch updated question info based on questionInfoIds
  useEffect(() => {
    const fetchQuestionInfo = async () => {
      if (!state.questionInfoList || state.questionInfoList.length === 0) {
        return;
      }

      try {
        setIsFetching(true);
        const questionIds = state.questionInfoList.map((q: QuestionInfoExt) => q.id!);

        // Fetch all questions in parallel
        const questionPromises = questionIds.map((id: number) =>
          getQuestionInfo({ questionId: id })
        );

        const responses = await Promise.all(questionPromises);

        // Extract question data from responses and convert to QuestionInfoExt
        const updatedQuestions = responses
          .filter(res => res.code === 200 && res.data?.question)
          .map(res => {
            const question = res.data!.question;
            const labels = res.data!.questionLabelList || [];

            // Extract topic and knowledge point info from labels
            const topicIds: number[] = [];
            const topicNames: string[] = [];
            const knowledgePointIds: number[] = [];
            const knowledgeNames: string[] = [];

            labels.forEach(label => {
              if (label.type === 'topic' && label.id) {
                topicIds.push(label.id);
                if (label.name) topicNames.push(label.name);
              } else if (label.type === 'knowledge_point' && label.id) {
                knowledgePointIds.push(label.id);
                if (label.name) knowledgeNames.push(label.name);
              }
            });

            // Combine QuestionInfo with extended fields
            return {
              ...question,
              topicIds,
              topicNames,
              knowledgePointIds,
              knowledgeNames,
            } as QuestionInfoExt;
          });

        if (updatedQuestions.length > 0) {
          setSelectedQuestions(updatedQuestions);
        }
      } catch (error) {
        console.error('Failed to fetch question info:', error);
        toast.error(t('common.error') + ': Failed to load questions');
      } finally {
        setIsFetching(false);
      }
    };

    fetchQuestionInfo();
  }, []);

  useEffect(() => {
    installMathFieldWrapper();
  }, [selectedQuestions]);

  // 获取知识点数据
  const { data: knowledgePointData, error: knowledgePointError, isLoading: knowledgePointLoading } = useGetKnowledgePointList({
    teacherId: getCurrentUser()!.id,
    schoolName: getCurrentUser()!.schoolName,
    topicId: ''
  });

  // 构建级联选择器的选项数据 - 基于用户之前选中的 Topic 和 KnowledgePoint
  const cascadingOptions = React.useMemo((): CascadingOption[] => {
    if (!allTopics.length || !knowledgePointData?.data?.knowledgePoints) {
      return [];
    }

    // 从全局数据中筛选出用户选中的 Topic
    const selectedTopics = allTopics.filter(topic => state.topicIds?.includes(topic.id!));
    
    // 从全局数据中筛选出用户选中的 KnowledgePoint
    const selectedKnowledgePoints = knowledgePointData.data.knowledgePoints.filter(kp => 
      state.knowledgePointIds?.includes(kp.id!)
    );

    return selectedTopics.map(topic => ({
      label: isEN(i18n) ? (topic.enName || topic.name!) : topic.name!,
      value: topic.id!.toString(),
      children: selectedKnowledgePoints
        .filter(kp => kp.mtrId && kp.mtrId.startsWith(topic.mtrId + '-'))
        .map(kp => ({
          label: isEN(i18n) ? (kp.enName || kp.name!) : kp.name!,
          value: kp.id!.toString(),
        }))
    })).filter(option => option.children && option.children.length > 0);
  }, [allTopics, knowledgePointData, state.topicIds, state.knowledgePointIds, i18n]);

  useEffect(() => {
    const loadTopicData = async () => {
      try {
        // Check if state and required data exist
        if (!state || !state.topicIds) {
          console.warn('Missing state or topicIds');
          return;
        }

        // 获取所有topic列表
        const topicListRsp = await getTopicList({
          teacherId: getCurrentUser()!.id,
          schoolName: getCurrentUser()!.schoolName
        });

        if (topicListRsp.code === 200 && topicListRsp.data) {
          setAllTopics(topicListRsp.data.topicInfos);

          // 筛选出当前请求中的topics
          const requestTopics = topicListRsp.data.topicInfos.filter(topic =>
            state.topicIds.includes(topic.id!)
          );
          setCurrentTopics(requestTopics);
        }
      } catch (error) {
        console.log('Load topic data error', error);
      }
    };

    loadTopicData();
  }, [state]);

  const getQuestionTopics = (questionInfo: QuestionInfoExt): string[] => {
    return questionInfo.topicNames || [];
  }

  const getQuestionKnowledgePoints = (questionInfo: QuestionInfoExt): string[] => {
    return questionInfo.knowledgeNames || [];
  }

  const createTableData = (questionInfo: QuestionInfoExt): TableData => {
    return {
      questionText: getQuestionText(questionInfo, i18n, state.language),
      questionInfo: questionInfo,
      difficulty: questionInfo.difficultyLevel || 0,
      topics: getQuestionTopics(questionInfo),
      knowledgePoints: getQuestionKnowledgePoints(questionInfo)
    };
  }

  const getFilteredQuestions = () => {
    if (knowledgePointFilter.length === 0) {
      return selectedQuestions;
    }

    return selectedQuestions.filter((question: QuestionInfoExt) => {
      return knowledgePointFilter.some(filterId => {
        const id = parseInt(filterId);
        if (question.topicIds?.includes(id)) return true;
        if (question.knowledgePointIds?.includes(id)) return true;
        return false;
      });
    });
  }

  const goEdit = async () => {
    if (!state) {
      console.error('Cannot edit: missing state data');
      return;
    }
    navigate(ROUTE.TEACHER.HOMEWORK_MANUAL_EDIT, {
      state: {
        ...state,
        draftId: draftId, // Pass along the draft ID for tracking
      }
    });
  }

  const handleSaveDraft = () => {
    if (!state) {
      toast.error(t('common.error'));
      return;
    }

    try {
      // saveHomeworkDraft uses draftId for ID-based update, falls back to hash matching
      saveHomeworkDraft({
        title: state.title || '',
        description: state.description,
        topicIds: state.topicIds || [],
        knowledgePointIds: state.knowledgePointIds,
        libraryNames: state.libraryNames || [],
        subjectId: state.subjectId,
        subjectName: subjectName,
        classInfos: state.classInfos || [],
        dueDate: state.dueDate ? state.dueDate.toISOString() : new Date().toISOString(),
        questionInfoList: selectedQuestions,
        language: state.language,
        shareTeacherIds: state.shareTeacherIds,
        currentPage: 'aieditresult',
      }, draftId);
      toast.success(t('teacher.draftsaved') || 'Draft saved! Will be retained for 24 hours.');
    } catch (error) {
      toast.error(t('common.error'));
    }
  }

  const onPublish = () => execute(async () => {
    if (!subjectName) {
      toast.error(t('common.loading') || 'Subject data is loading, please wait.');
      return;
    }
    if (!state || !state.classInfos || selectedQuestions.length === 0) {
      alert(t('common.error') + ': Missing required data');
      return;
    }

    const quizRsp = await createQuiz({
      title: state.title || '',
      description: state.description || '',
      subject: subjectName,
      subjectId: state.subjectId,
      originName: getCurrentUser()!.schoolName,
      originType: "school",
      language: state.language,
      teacher_id: getCurrentUser()!.id,
      topicIds: state.topicIds || [],
      questionInfoIds: selectedQuestions.map(question => question.id!),
    });
    for (const classInfo of state.classInfos) {
      const rsp = await createClassroom({
        classroomType: 0,
        classroomInfoId: classInfo.id,
        teacherId: getCurrentUser()!.id,
        subject: subjectName,
        subjectId: state.subjectId,
      });
      const ret = await publishAssignment({
        title: state.title || '',
        description: state.description || '',
        type: "homework",
        teacherId: getCurrentUser()!.id,
        classInfoId: classInfo.id,
        classSessionId: rsp.data!.sessionId,
        quizId: quizRsp.data!.id,
        dueDate: format(state.dueDate ?? new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        topicIds: state.topicIds || [],
      });
      if (ret.code === 200 && ret.data) {
        // 每个班级创建的作业都分享
        if (state.shareTeacherIds?.length) {
          await updateShareGrantees({
            assignmentId: ret.data,
            granteeIds: state.shareTeacherIds.map(id => Number(id)),
          });
        }
      }
      console.log('publish assignment ', ret, rsp.data!.sessionId);
    }
    alert(t('teacher.publishsuccess'));
    if (draftId) {
      removeHomeworkDraft(draftId);
    }
    navigate(ROUTE.TEACHER.OVERVIEW);
  });

  const columns: ColumnDef<TableData>[] = [
    {
      id: "actions",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // Navigate to question creation with AI generation mode
            // Pass the current homework state for resuming after question creation
            const topicId = state.topicIds?.[0];
            const topicName = currentTopics?.[0]?.name || '';

            if (!topicId) {
              toast.error(t('common.error') + ': Missing topic');
              return;
            }

            console.log('Go to edit question, current state: ', row.original.questionInfo);
            navigate(ROUTE.TEACHER.LIBRARY_QUESTION_EDIT, {
              state: {
                isEditMode: true,
                topicId: topicId,
                topicName: topicName,
                questionId: row.original.questionInfo.id,
                defaultValue: row.original.questionInfo,
                isAIGenerate: true,
                isAIGenerated: true,
                resumeHomeworkState: {
                  ...state,
                  questionInfoList: selectedQuestions,
                },
              }
            });
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ),
    },
    {
      id: "select",
      header: ({ table }) => (
        //   <Checkbox
        //     checked={
        //       table.getIsAllPageRowsSelected() ||
        //       (table.getIsSomePageRowsSelected() && "indeterminate")
        //     }
        //     onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        //     aria-label="Select all"
        //   />
        <></>
      ),
      cell: ({ row }) => (
        //   <Checkbox
        //     checked={row.getIsSelected()}
        //     onCheckedChange={(value) => row.toggleSelected(!!value)}
        //     aria-label="Select row"
        //   />
        <></>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "questionText",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("common.question")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => {
        return (
          <math-field read-only className="w-xs break-all border-gray-200">
            {convertSpacesToLatex(getQuestionText(row.original.questionInfo, i18n, state.language))}
          </math-field>
        )
      },
    },
    {
      accessorKey: "difficulty",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("common.difficulty")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => {
        const difficulty = row.original.difficulty;
        return <div>{difficultyLevelToText(difficulty, i18n)}</div>;
      },
    },
    {
      accessorKey: "topics",
      header: () => { return (<span>{t("common.topic")}</span>) },
      cell: ({ row }) => (
        <div className="max-w-xs">
          {row.original.topics.length > 0 ? row.original.topics.join(", ") : "-"}
        </div>
      ),
    },
    {
      accessorKey: "knowledgePoints",
      header: () => { return (<span>{t("common.knowledgepoint")}</span>) },
      cell: ({ row }) => (
        <div className="whitespace-normal break-keep max-w-xs">
          {row.original.knowledgePoints.length > 0 ? row.original.knowledgePoints.join(", ") : "-"}
        </div>
      ),
    },
    {
      accessorKey: "standardAnswer",
      header: () => { return (<span>{t("common.answer")}</span>) },
      cell: ({ row }) => (
        <math-field read-only className="w-xs break-all border-gray-200">
          {convertSpacesToLatex(getStandardAnswerText(row.original.questionInfo, i18n, state.language))}
        </math-field>
      ),
    },
    {
      accessorKey: "type",
      header: () => { return (<span>{t("teacher.qtype")}</span>) },
      cell: ({ row }) => (
        <div>{t("common." + row.original.questionInfo.questionType!.toLowerCase())}</div>
      ),
    },
  ];

  const selectedColumn = Array.from(columns);

  // Handle case where state is missing or invalid
  if (!state) {
    return (
      <ContentLayout title={t('teacher.questionlist')}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-lg text-gray-600">{t('common.error')}</p>
            <p className="text-sm text-gray-500">Missing assignment data</p>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="mt-4"
            >
              {t('common.goprev')}
            </Button>
          </div>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout title={t('teacher.questionlist')}>
      <LoadingOverlay isLoading={isLoading} text={t('common.submitting')} />
      {/* <Label>topic:</Label> */}
      <div className='w-full bg-background'>

        <div className='p-4'>
          <DataTable
            data={getFilteredQuestions().map(createTableData)}
            columns={selectedColumn}
            filterColumn='questionText'
            filterName={t('common.question')}
            renderButton={() => (
              <CascadingSelect
                className="w-[200px]"
                options={cascadingOptions}
                value={knowledgePointFilter}
                onChange={setKnowledgePointFilter}
                placeholder={t('component.selectknowledgepoint')}
              />
            )}
          />
        </div>
      </div>

      <div className='flex place-content-evenly pb-16'>
        <Button variant="secondary" onClick={() => { navigate(-1); }}>{t('common.goprev')}</Button>
        <Button variant="secondary" onClick={() => { handleSaveDraft(); }}>{t('teacher.savedraft')}</Button>
        <Button variant="secondary" onClick={() => { goEdit(); }}>{t('common.edit')}</Button>
        <Button variant="default" className='bg-primary' onClick={() => {
          if (selectedQuestions.length === 0) {
            alert(t('teacher.alert'));
            return;
          }
          setShowPreviewDialog(true);
        }}>{t('teacher.publish')}</Button>
      </div>

      {/* 预览弹窗 */}
      <HomeworkPreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        questions={selectedQuestions}
        mode={PreviewMode.QUIZ}
        title={state.title || ''}
        description={state.description}
        subject={subjectName}
        unit={currentTopics.map(topic => topic.name).join(', ')}
        className={state.classInfos?.map(classInfo => classInfo.name).join(', ')}
        dueDate={state.dueDate}
        onConfirm={onPublish}
        isLoading={isLoading}
        language={state.language}
      />
    </ContentLayout>
  );
};

export default AICompileAssignmentResultPage;